import { useCallback, useMemo, useState } from 'react';

import { useGetRunningTasksQuery } from '../../api/variantApi';
import { GetVariantsArgs } from '../../api/variantCheckApi';
import {
  RUNNING_TASK_POLLING_INTERVAL,
  RUNNING_TASK_TYPES,
  VARIANT_CREATION_TASK_TYPES,
} from '../../running_tasks/constants';

const PAGE_SIZE = 20;

export const useVariantCheckLogic = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState<undefined | string>(undefined);

  const [queryArgs, setQueryArgs] = useState<GetVariantsArgs>({
    limit: PAGE_SIZE,
    offset: 0,
  });

  const { data: runningTasks = [] } = useGetRunningTasksQuery(undefined, {
    pollingInterval: RUNNING_TASK_POLLING_INTERVAL,
  });

  const activeRefreshVariantIds = useMemo(
    () =>
      new Set(
        runningTasks
          .filter(
            (task) =>
              task.task_type === RUNNING_TASK_TYPES.REFRESH_AND_RECALCULATE &&
              task.status === 'running' &&
              task.variant_id != null,
          )
          .map((task) => task.variant_id)
          .filter((variantId): variantId is number => variantId != null),
      ),
    [runningTasks],
  );

  // Variants that are still being created (variant_create /
  // vcf_variant_create tasks). While one of these is running the variants
  // list is polled so the "creating" row shows up — and clears — even if the
  // creation was started in another browser tab.
  const hasRunningCreationTasks = useMemo(
    () =>
      runningTasks.some(
        (task) =>
          VARIANT_CREATION_TASK_TYPES.includes(task.task_type) &&
          task.status === 'running',
      ),
    [runningTasks],
  );

  const hasRecalculatingVariant = activeRefreshVariantIds.size > 0;

  const fetchData = useCallback(
    (
      offset = 0,
      query?: string,
      sortField?: string,
      sortOrder?: 'ascend' | 'descend',
      limit?: number,
    ) => {
      setQueryArgs({
        offset,
        limit: limit ?? pageSize,
        search: query ? query : search,
        sortField,
        sortOrder,
      });
    },
    [search, pageSize],
  );

  const handleSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
  };

  const resetSearch = () => {
    setSearch('');
    setCurrentPage(1);
    setQueryArgs((previous) => ({ ...previous, offset: 0, search: undefined }));
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData();
  };

  return {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    queryArgs,
    setQueryArgs,
    hasRecalculatingVariant,
    hasRunningCreationTasks,
    activeRefreshVariantIds,
    fetchData,
    handleSearchValueChange,
    handleSearch,
    resetSearch,
    PAGE_SIZE,
  };
};
